import { BaseLayouter } from './BaseLayouter';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { LayoutData, LineDirectionType } from '../LayoutData';
import { AlignmentMode } from '../../field/FieldTypes';
import { UpVectorMode } from '../../field/FieldTypes';
import * as THREE from 'three';

export class LineLayouter extends BaseLayouter {
    public readonly defaultSpacing: number = 3.0;
    public readonly defaultDirection: Vector3 = new Vector3(0, 0, 1);
    public readonly defaultRotationStep: number = 0;
    
    constructor() {
        super(
            'line',
            'Universal line layouter - supports any linear pattern with Vector3 direction',
            {
                lineSpacing: 3.0,
                lineDirection: new Vector3(0, 0, 1),
                rotationStep: 0,
                alignmentMode: AlignmentMode.Aligned,
                lineCenterLayout: false
            }
        );
    }
    
    /** Seeded PRNG (mulberry32) for deterministic jitter */
    private seededRandom(seed: number): () => number {
        return () => {
            seed |= 0; seed = seed + 0x6D2B79F5 | 0;
            let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
            t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
            return ((t ^ t >>> 14) >>> 0) / 4294967296;
        };
    }

    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const spacing = this.getParameter(layoutData, 'lineFixedSpacing', this.defaultSpacing);
        const direction = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        const centerLayout = this.getParameter(layoutData, 'lineCenterLayout', false);
        const jitter = this.getParameter(layoutData, 'lineJitter', 0);
        const jitterSeed = this.getParameter(layoutData, 'lineJitterSeed', 42);

        // All objects arranged in line (including index 0)
        const adjustedIndex = index;
        const adjustedTotal = totalCount;

        // Calculate offset based on center layout setting
        const offset = centerLayout
            ? (adjustedIndex - (adjustedTotal - 1) / 2) * spacing  // Centered around origin
            : adjustedIndex * spacing;  // Start from origin (0,0,0)

        // Use normalized direction vector for positioning (clone to avoid mutation)
        const normalizedDirection = direction.clone().normalize();
        const position = normalizedDirection.multiplyScalar(offset);

        // Apply perpendicular jitter if set (organic offset from the line)
        if (jitter > 0) {
            const rng = this.seededRandom(jitterSeed + index * 7919);
            // Build perpendicular axes from direction
            const up = new Vector3(0, 1, 0);
            const right = new Vector3().crossVectors(normalizedDirection, up).normalize();
            if (right.length() < 0.01) {
                // Direction is parallel to up — use X as fallback
                right.crossVectors(normalizedDirection, new Vector3(1, 0, 0)).normalize();
            }
            const perp = new Vector3().crossVectors(right, normalizedDirection).normalize();
            const jx = (rng() - 0.5) * 2 * jitter;
            const jy = (rng() - 0.5) * 2 * jitter;
            position.x += right.x * jx + perp.x * jy;
            position.y += right.y * jx + perp.y * jy;
            position.z += right.z * jx + perp.z * jy;
        }

        return position;
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        const direction = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        
        // ============ PROVEN 1D ORIENTATION LOGIC FROM WORKING DEMO ============
        
        // 1. Calculate tangent vector (normalized line direction)
        const tangent = direction.clone().normalize();
        
        // 2. Calculate normal vector (perpendicular to line, horizontal preference)
        let normal: Vector3;
        if (Math.abs(tangent.y) < 0.9) {
            // Line is not vertical - use world up to get horizontal normal
            normal = new Vector3().crossVectors(tangent, new Vector3(0, 1, 0));
            if (normal.length() < 0.001) {
                normal = new Vector3(1, 0, 0); // Fallback
            }
        } else {
            // Line is vertical - use world right to get normal
            normal = new Vector3().crossVectors(tangent, new Vector3(1, 0, 0));
            if (normal.length() < 0.001) {
                normal = new Vector3(0, 0, 1); // Fallback
            }
        }
        normal.normalize();
        
        // 3. Calculate binormal vector (perpendicular to both tangent and normal)
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        // 4. Determine forward direction based on alignment mode
        let desiredForward: Vector3;
        if (alignmentMode === AlignmentMode.Aligned) {
            // ALIGNED: Object follows the line direction (uses normal - horizontal direction)
            desiredForward = normal.clone();
        } else {
            // PERPENDICULAR: Object is perpendicular to line (uses tangent - line direction)
            desiredForward = tangent.clone();
        }
        
        // 5. Determine up direction based on up vector mode
        let desiredUp: Vector3;
        if (upVectorMode === UpVectorMode.WorldUp) {
            desiredUp = new Vector3(0, 1, 0);
        } else {
            // PATH UP: Use binormal (perpendicular to line plane), negated for correct orientation
            desiredUp = binormal.clone().negate();
        }
        
        // 6. Build orthogonal coordinate system using proven logic
        desiredForward.normalize();
        desiredUp.normalize();
        
        // Check for parallelism (degenerative case)
        const dot = Math.abs(desiredForward.dot(desiredUp));
        if (dot > 0.99) {
            if (upVectorMode === UpVectorMode.WorldUp) {
                // Forward is parallel to world up - adjust
                if (Math.abs(desiredForward.y) > 0.9) {
                    desiredForward.set(1, 0, 0); // Use world X if forward is mostly Y
                }
            }
        }
        
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Up must stay (0,1,0), calculate right and forward accordingly
            finalUp = new Vector3(0, 1, 0);
            
            // For lines with World-Up + Aligned, we want the object to be perfectly upright
            // The forward direction should be the horizontal projection of the desired forward
            if (alignmentMode === AlignmentMode.Aligned) {
                // For aligned mode: project the normal (horizontal direction) onto horizontal plane
                finalForward = new Vector3(desiredForward.x, 0, desiredForward.z);
                if (finalForward.length() < 0.001) {
                    // If desired forward is purely vertical, use a fallback horizontal direction
                    finalForward = new Vector3(1, 0, 0);
                }
                finalForward.normalize();
                
                // Calculate right: right = up × forward  
                finalRight = new Vector3().crossVectors(finalUp, finalForward);
                finalRight.normalize();
            } else {
                // For perpendicular mode: use the original logic
                finalRight = new Vector3().crossVectors(finalUp, desiredForward);
                if (finalRight.length() < 0.001) {
                    finalRight = new Vector3(1, 0, 0); // Fallback
                }
                finalRight.normalize();
                
                // Recalculate forward to ensure perfect orthogonality: forward = right × up
                finalForward = new Vector3().crossVectors(finalRight, finalUp);
                finalForward.normalize();
            }
        } else {
            // PATH UP: Use the normal approach with binormal as up
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            // Calculate right vector: right = up × forward
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            
            // Recalculate up to ensure orthogonality: up = forward × right
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        // 7. Convert orthogonal coordinate system to Euler angles (degrees)
        // Create rotation matrix from the orthogonal basis vectors
        const matrix = new THREE.Matrix4();
        matrix.makeBasis(
            new THREE.Vector3(finalRight.x, finalRight.y, finalRight.z),
            new THREE.Vector3(finalUp.x, finalUp.y, finalUp.z),
            new THREE.Vector3(finalForward.x, finalForward.y, finalForward.z)
        );
        
        // Extract Euler angles from rotation matrix
        const euler = new THREE.Euler();
        euler.setFromRotationMatrix(matrix, 'XYZ');
        
        // Convert to degrees and return as Vector3
        return new Vector3(
            euler.x * 180 / Math.PI,
            euler.y * 180 / Math.PI,
            euler.z * 180 / Math.PI
        );
    }
    
    validateLineParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        const direction = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        
        return spacing > 0 && 
               direction instanceof Vector3 && direction.length() > 0;
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        return super.validateParameters(layoutData) && this.validateLineParameters(layoutData);
    }
    
    // Get total line dimensions
    getLineLength(objectCount: number, layoutData: LayoutData): number {
        const spacing = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        return (objectCount - 1) * spacing;
    }
    
    // Clone method
    clone(): LineLayouter {
        return new LineLayouter();
    }
    
    // Factory method
    static create(): LineLayouter {
        return new LineLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // CONSISTENT FIRST ORIENTATION: Index 0 has no rotation
        if (index === 0) {
            return Quaternion.identity();
        }
        
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        const direction = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        
        // ============ PROVEN 1D ORIENTATION LOGIC FROM WORKING DEMO ============
        
        // 1. Calculate tangent vector (normalized line direction)
        const tangent = direction.clone().normalize();
        
        // 2. Calculate normal vector (perpendicular to line, horizontal preference)
        let normal: Vector3;
        if (Math.abs(tangent.y) < 0.9) {
            // Line is not vertical - use world up to get horizontal normal
            normal = new Vector3().crossVectors(tangent, new Vector3(0, 1, 0));
            if (normal.length() < 0.001) {
                normal = new Vector3(1, 0, 0); // Fallback
            }
        } else {
            // Line is vertical - use world right to get normal
            normal = new Vector3().crossVectors(tangent, new Vector3(1, 0, 0));
            if (normal.length() < 0.001) {
                normal = new Vector3(0, 0, 1); // Fallback
            }
        }
        normal.normalize();
        
        // 3. Calculate binormal vector (perpendicular to both tangent and normal)
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        // 4. Determine forward direction based on alignment mode
        let desiredForward: Vector3;
        if (alignmentMode === AlignmentMode.Aligned) {
            // ALIGNED: Object follows the line direction (uses normal - horizontal direction)
            desiredForward = normal.clone();
        } else {
            // PERPENDICULAR: Object is perpendicular to line (uses tangent - line direction)
            desiredForward = tangent.clone();
        }
        
        // 5. Determine up direction based on up vector mode
        let desiredUp: Vector3;
        if (upVectorMode === UpVectorMode.WorldUp) {
            desiredUp = new Vector3(0, 1, 0);
        } else {
            // PATH UP: Use binormal (perpendicular to line plane), negated for correct orientation
            desiredUp = binormal.clone().negate();
        }
        
        // 6. Build orthogonal coordinate system using proven logic
        desiredForward.normalize();
        desiredUp.normalize();
        
        // Check for parallelism (degenerative case)
        const dot = Math.abs(desiredForward.dot(desiredUp));
        if (dot > 0.99) {
            if (upVectorMode === UpVectorMode.WorldUp) {
                // Forward is parallel to world up - adjust
                if (Math.abs(desiredForward.y) > 0.9) {
                    desiredForward.set(1, 0, 0); // Use world X if forward is mostly Y
                }
            }
        }
        
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Up must stay (0,1,0), calculate right and forward accordingly
            finalUp = new Vector3(0, 1, 0);
            
            // Calculate right: right = up × forward
            finalRight = new Vector3().crossVectors(finalUp, desiredForward);
            if (finalRight.length() < 0.001) {
                finalRight = new Vector3(1, 0, 0); // Fallback
            }
            finalRight.normalize();
            
            // Recalculate forward to ensure perfect orthogonality: forward = right × up
            finalForward = new Vector3().crossVectors(finalRight, finalUp);
            finalForward.normalize();
        } else {
            // PATH UP: Use the normal approach with binormal as up
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            // Calculate right vector: right = up × forward
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            
            // Recalculate up to ensure orthogonality: up = forward × right
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        // 7. Create quaternion from orthogonal coordinate system
        return Quaternion.lookDir(finalForward, finalUp);
    }
    
    /**
     * Calculate Frenet frame for line (1D curve)
     * For straight lines, normal and binormal are derived from tangent
     */
    private calculateLineFrenetFrame(direction: Vector3): {
        tangent: Vector3,
        normal: Vector3,
        binormal: Vector3
    } {
        // Tangent vector is the normalized direction
        const tangent = direction.clone().normalize();
        
        // For straight line, normal is perpendicular to tangent
        let normal: Vector3;
        if (Math.abs(tangent.y) < 0.9) {
            normal = new Vector3().crossVectors(tangent, new Vector3(0, 1, 0)).normalize();
        } else {
            normal = new Vector3().crossVectors(tangent, new Vector3(1, 0, 0)).normalize();
        }
        
        // Binormal = tangent × normal
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        return { tangent, normal, binormal };
    }
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'lineFixedSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        // For lines, uniform spacing doesn't make sense since lines are infinite
        // Return fixed spacing as fallback
        return this.getFixedSpacingParameter(layoutData);
    }

    /**
     * Create debug visualization for the line path
     */
    public createDebugVisualization(layoutData: LayoutData): THREE.Group | null {
        // Fallback method - use estimated object count
        return this.createDebugVisualizationWithObjectCount(layoutData, 8);
    }

    /**
     * Create debug visualization with REAL object count
     */
    public createDebugVisualizationWithObjectCount(layoutData: LayoutData, objectCount: number): THREE.Group | null {
        const debugGroup = new THREE.Group();
        debugGroup.name = 'LineLayouter_Debug';

        // Get line parameters
        const lineDir = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        const spacing = this.getParameter(layoutData, 'lineFixedSpacing', this.defaultSpacing);
        
        // Calculate line length based on REAL object count
        const lineLength = spacing * (objectCount - 1);
        
        // Calculate start and end positions based on direction
        let directionVector: Vector3;
        switch (lineDir) {
            case 'x': case LineDirectionType.X: directionVector = new Vector3(1, 0, 0); break;
            case '-x': case LineDirectionType.NegativeX: directionVector = new Vector3(-1, 0, 0); break;
            case 'y': case LineDirectionType.Y: directionVector = new Vector3(0, 1, 0); break;
            case '-y': case LineDirectionType.NegativeY: directionVector = new Vector3(0, -1, 0); break;
            case 'z': case LineDirectionType.Z: directionVector = new Vector3(0, 0, 1); break;
            case '-z': case LineDirectionType.NegativeZ: directionVector = new Vector3(0, 0, -1); break;
            default: directionVector = new Vector3(0, 0, 1); break;
        }
        
        const halfLength = lineLength / 2;
        const startPos = new Vector3().copy(directionVector).multiplyScalar(-halfLength);
        const endPos = new Vector3().copy(directionVector).multiplyScalar(halfLength);
        
        if (!startPos || !endPos) {
            return null;
        }

        // Create line geometry
        const points = [
            new THREE.Vector3(startPos.x, startPos.y, startPos.z),
            new THREE.Vector3(endPos.x, endPos.y, endPos.z)
        ];
        
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const lineMaterial = new THREE.LineBasicMaterial({ 
            color: 0x00ff00,  // Green for line path
            linewidth: 3,
            transparent: true,
            opacity: 0.8
        });
        
        const debugLine = new THREE.Line(lineGeometry, lineMaterial);
        debugLine.name = 'DebugLinePath';
        
        // Add position markers along the line
        for (let i = 0; i < objectCount; i++) {
            const t = objectCount > 1 ? i / (objectCount - 1) : 0;
            const markerPos = new THREE.Vector3().lerpVectors(
                new THREE.Vector3(startPos.x, startPos.y, startPos.z),
                new THREE.Vector3(endPos.x, endPos.y, endPos.z),
                t
            );
            
            const markerGeometry = new THREE.SphereGeometry(0.1);
            const markerMaterial = new THREE.MeshBasicMaterial({ 
                color: i === 0 ? 0x00ff00 : (i === objectCount - 1 ? 0xff0000 : 0xffff00),
                transparent: true,
                opacity: 0.8
            });
            const marker = new THREE.Mesh(markerGeometry, markerMaterial);
            marker.position.copy(markerPos);
            marker.name = `LineMarker_${i}`;
            
            debugGroup.add(marker);
        }
        
        debugGroup.add(debugLine);
        
        return debugGroup;
    }

    /**
     * Create an arrow helper
     */
    private createArrow(position: Vector3, direction: Vector3, size: number, color: number): THREE.Group {
        const arrowGroup = new THREE.Group();
        
        // Arrow shaft
        const shaftGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 0, size)
        ]);
        const shaftMaterial = new THREE.LineBasicMaterial({ color: color, linewidth: 2 });
        const shaft = new THREE.Line(shaftGeometry, shaftMaterial);
        
        // Arrow head
        const headGeometry = new THREE.ConeGeometry(size * 0.2, size * 0.3, 8);
        const headMaterial = new THREE.MeshBasicMaterial({ color: color });
        const head = new THREE.Mesh(headGeometry, headMaterial);
        head.position.set(0, 0, size);
        head.rotateX(-Math.PI / 2);
        
        arrowGroup.add(shaft);
        arrowGroup.add(head);
        
        // Position and orient the arrow
        arrowGroup.position.set(position.x, position.y, position.z);
        
        // Orient arrow to point in direction
        const quaternion = new THREE.Quaternion().setFromUnitVectors(
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(direction.x, direction.y, direction.z)
        );
        arrowGroup.setRotationFromQuaternion(quaternion);
        
        return arrowGroup;
    }
}

export class UphillLayouter extends BaseLayouter {
    public readonly defaultSpacing: number = 3.0;
    public readonly defaultHeight: number = 0.5;
    
    constructor() {
        super(
            'uphill',
            'Arranges objects in an ascending line (uphill pattern)',
            {
                lineSpacing: 3.0,
                spiralHeight: 0.5
            }
        );
    }
    
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const step = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        const height = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        
        // CONSISTENT FIRST POSITION: Index 0 always at origin (0,0,0)
        if (index === 0) {
            return new Vector3(0, 0, 0);
        }
        
        // Remaining objects arranged uphill (starting from index 1)
        const adjustedIndex = index - 1;
        return new Vector3(0, adjustedIndex * height, -adjustedIndex * step);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        // Uphill objects face forward
        return Vector3.zero();
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        const height = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        
        return super.validateParameters(layoutData) && spacing > 0 && height >= 0;
    }
    
    // Clone method
    clone(): UphillLayouter {
        return new UphillLayouter();
    }
    
    // Factory method
    static create(): UphillLayouter {
        return new UphillLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        // For uphill layout, uniform spacing doesn't make much sense, so return fixed spacing
        return this.getFixedSpacingParameter(layoutData);
    }
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // Uphill objects face forward (no rotation)
        return Quaternion.identity();
    }
}

export class UphillRotatedLayouter extends BaseLayouter {
    public readonly defaultSpacing: number = 3.0;
    public readonly defaultHeight: number = 0.5;
    public readonly defaultRotationStep: number = 10;
    
    constructor() {
        super(
            'uphill_rotated',
            'Arranges objects in an ascending line with incremental rotation',
            {
                lineSpacing: 3.0,
                spiralHeight: 0.5,
                rotationStep: 10
            }
        );
    }
    
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const step = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        const height = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        
        return new Vector3(0, index * height, -index * step);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const rotationStep = this.getParameter(layoutData, 'rotationStep', this.defaultRotationStep);
        const zRotation = index * rotationStep;
        
        return new Vector3(0, 0, zRotation);
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        const height = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        
        return super.validateParameters(layoutData) && spacing > 0 && height >= 0;
    }
    
    // Clone method
    clone(): UphillRotatedLayouter {
        return new UphillRotatedLayouter();
    }
    
    // Factory method
    static create(): UphillRotatedLayouter {
        return new UphillRotatedLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        return this.getFixedSpacingParameter(layoutData);
    }
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        const rotationStep = this.getParameter(layoutData, 'rotationStep', this.defaultRotationStep);
        const adjustedIndex = index === 0 ? 0 : index - 1;
        const yRotation = adjustedIndex * rotationStep;
        return Quaternion.fromEulerAnglesDegrees(new Vector3(0, yRotation, 0));
    }
}

export class HorizontalLayouter extends BaseLayouter {
    public readonly defaultSpacing: number = 3.0;
    public readonly defaultHeight: number = 0;
    public readonly defaultRotationStep: number = 0;
    
    constructor() {
        super(
            'horizontal',
            'Arranges objects horizontally along X-axis with optional rotation',
            {
                lineSpacing: 3.0,
                spiralHeight: 0,
                rotationStep: 0
            }
        );
    }
    
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const spacing = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        const height = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        
        // CONSISTENT FIRST POSITION: Index 0 always at origin (0,0,0)
        if (index === 0) {
            return new Vector3(0, height, 0);
        }
        
        // Remaining objects arranged horizontally (starting from index 1)
        const adjustedIndex = index - 1;
        const adjustedTotal = totalCount - 1;
        
        // Center horizontally
        const x = (adjustedIndex - (adjustedTotal - 1) / 2) * spacing;
        const y = height;
        
        return new Vector3(x, y, 0);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const rotationStep = this.getParameter(layoutData, 'rotationStep', this.defaultRotationStep);
        const yRotation = index * rotationStep;
        
        return new Vector3(0, yRotation, 0);
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        
        return super.validateParameters(layoutData) && spacing > 0;
    }
    
    // Clone method
    clone(): HorizontalLayouter {
        return new HorizontalLayouter();
    }
    
    // Factory method
    static create(): HorizontalLayouter {
        return new HorizontalLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        return this.getFixedSpacingParameter(layoutData);
    }
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        const rotationStep = this.getParameter(layoutData, 'rotationStep', this.defaultRotationStep);
        const adjustedIndex = index === 0 ? 0 : index - 1;
        const yRotation = adjustedIndex * rotationStep;
        return Quaternion.fromEulerAnglesDegrees(new Vector3(0, yRotation, 0));
    }
}

export class VerticalLayouter extends BaseLayouter {
    public readonly defaultSpacing: number = 3.0;
    public readonly defaultRotationStep: number = 0;
    
    constructor() {
        super(
            'vertical',
            'Arranges objects vertically along Y-axis with optional rotation',
            {
                lineSpacing: 3.0,
                rotationStep: 0
            }
        );
    }
    
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const spacing = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        
        // Center vertically
        const y = (index - (totalCount - 1) / 2) * spacing;
        
        return new Vector3(0, y, 0);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const rotationStep = this.getParameter(layoutData, 'rotationStep', this.defaultRotationStep);
        const yRotation = index * rotationStep;
        
        return new Vector3(0, yRotation, 0);
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
        
        return super.validateParameters(layoutData) && spacing > 0;
    }
    
    // Clone method
    clone(): VerticalLayouter {
        return new VerticalLayouter();
    }
    
    // Factory method
    static create(): VerticalLayouter {
        return new VerticalLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'lineSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        return this.getFixedSpacingParameter(layoutData);
    }
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        const rotationStep = this.getParameter(layoutData, 'rotationStep', this.defaultRotationStep);
        const adjustedIndex = index === 0 ? 0 : index - 1;
        const yRotation = adjustedIndex * rotationStep;
        return Quaternion.fromEulerAnglesDegrees(new Vector3(0, yRotation, 0));
    }
} 